/**
 * The compiled survey as the report reads it (F5′). Twins meet an ad, a page
 * or a message by its ordinal ("Ad 2"), so the compiled question copy names
 * it that way; the compiler also writes the report's copy of each title and
 * description on the question map (`reportTitle`, `reportDescription`), which
 * names the asset as readers know it ("Sausage biscuit", the shared
 * `assetCatalogTitle`). This is the one place the
 * report takes it: every report surface (question cards, the Survey Data tab,
 * figure and split titles, navigation, the twin chat's question context)
 * reads the survey this returns. Nothing here reads the prose: a question
 * without a report copy keeps its own words, as every survey compiled before
 * F5′ named its assets already. The stored survey is never changed.
 */

type JsonRecord = Record<string, any>

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function withReportCopy(element: unknown, questionMap: JsonRecord): unknown {
  if (!isRecord(element)) return element
  const nested = Array.isArray(element.elements)
    ? { elements: element.elements.map((child: unknown) => withReportCopy(child, questionMap)) }
    : {}
  const entry = typeof element.name === 'string' ? questionMap[element.name] : undefined
  const reportTitle = isRecord(entry) && typeof entry.reportTitle === 'string' ? entry.reportTitle : null
  const reportDescription =
    isRecord(entry) && typeof entry.reportDescription === 'string' ? entry.reportDescription : null
  return {
    ...element,
    ...nested,
    ...(reportTitle ? { title: reportTitle } : {}),
    ...(reportDescription ? { description: reportDescription } : {}),
  }
}

export function reportSurveyView<T>(survey: T): T {
  if (!isRecord(survey)) return survey
  const questionMap = survey._canonicalConfig?.questionMap
  if (!isRecord(questionMap) || !Array.isArray(survey.pages)) return survey
  return {
    ...survey,
    pages: survey.pages.map((page: unknown) => withReportCopy(page, questionMap)),
  } as T
}
